import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Card } from 'primereact/card'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import { Messages } from 'primereact/messages'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../../components/Panel'
import { DropdownToggleable } from '../../../../../components/inputs'
import { languageOptions } from '../../../../../utils/i18n'
import { EmailChips } from '../../../../settings/EmailForm/EmailChips'
import { EditSupplierFormDto } from '../../data'
import { SupplierFormFieldName } from '../Supplier.data'

type Props = {
  formik: FormikProps<EditSupplierFormDto>
  separator: string
  isFormFieldInvalid: (name: SupplierFormFieldName) => boolean
  wrongInboundEmails: string[] | null
  wrongOutboundEmails: string[] | null
  getFormErrorMessage: (name: SupplierFormFieldName) => JSX.Element
  isEdit: boolean
  isRequestRunning: boolean
  isLoading: boolean
}
export const Email: React.FC<Props> = ({
  formik,
  separator,
  isFormFieldInvalid,
  wrongInboundEmails,
  wrongOutboundEmails,
  getFormErrorMessage,
  isEdit,
  isRequestRunning,
  isLoading,
}) => {
  const { t } = useTranslation()
  const panelId = 'supplier-email'
  const messagesRef = useRef<Messages>(null)

  useEffect(() => {
    if (messagesRef.current) {
      if (isEdit) {
        messagesRef.current.clear()
        messagesRef.current.show({
          id: '1',
          sticky: true,
          severity: 'info',
          detail: t('supplier.emailNotifications.message'),
          closable: false,
        })
      } else {
        messagesRef.current.clear()
      }
    }
  }, [isEdit])

  return (
    <PanelContainer
      header={t('supplier.emailNotifications.title')}
      id={panelId}
      toggleable={false}
    >
      <div className="grid">
        <div className="col-12 lg:col-4">
          <p>{t('supplier.emailNotifications.subtitle')}</p>
        </div>
        <div className="col-12 lg:col-7 lg:col-offset-1">
          <Card className="md:px-4">
            <Messages ref={messagesRef} />

            {/* Inbound */}
            <EmailChips
              id="inboundValue"
              label={t('supplier.emailNotifications.form.inbound.label')}
              value={formik.values.inboundValue}
              separator={separator}
              formik={formik}
              isInvalid={isFormFieldInvalid('inboundValue')}
              errorMessage={getFormErrorMessage('inboundValue')}
              wrongEmails={wrongInboundEmails}
              disabled={!isEdit || isRequestRunning}
              isLoading={isLoading}
            />

            {/* Outbound */}
            <EmailChips
              id="outboundValue"
              label={t('supplier.emailNotifications.form.outbound.label')}
              value={formik.values.outboundValue}
              separator={separator}
              formik={formik}
              isInvalid={isFormFieldInvalid('outboundValue')}
              errorMessage={getFormErrorMessage('outboundValue')}
              wrongEmails={wrongOutboundEmails}
              disabled={!isEdit || isRequestRunning}
              isLoading={isLoading}
            />

            {/* EmailLanguage */}
            <div className="formgrid grid">
              <div className="col-12 lg:col-6">
                <label
                  className={classNames('text-xs font-bold block mb-2', {
                    'p-error': isFormFieldInvalid('emailLanguageValue'),
                  })}
                  htmlFor="emailLanguageValue"
                >
                  {t('supplier.emailNotifications.form.language.label')}
                </label>
                <DropdownToggleable
                  checked={formik.values.emailLanguageOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue('emailLanguageOverride', e.checked)
                  }}
                  checkboxTooltip={t(
                    'supplier.emailNotifications.form.language.checkboxTooltip',
                  )}
                  id="emailLanguageValue"
                  options={languageOptions}
                  optionLabel="name"
                  optionValue="code"
                  placeholder="Sprache wählen …"
                  value={formik.values.emailLanguageValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid('emailLanguageValue'),
                  })}
                  disabled={!isEdit || isRequestRunning}
                />

                {getFormErrorMessage('emailLanguageValue')}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PanelContainer>
  )
}
